import React, { useCallback, useContext } from 'react'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import PrivateRequestFormModal from '../PrivateRequest/PrivateRequestFormModal'
import ModalContext from 'contexts/ModalContext'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import TextButton from 'LuxKit/Button/TextButton'
import ContactContext from 'contexts/contactContext'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'

interface Props {
  type: 'soldOut' | 'limited'
  variant: 'small' | 'medium' | 'large'
  offer: App.Tours.TourV2Offer
  hasPrivateRequest: boolean
  isPackageOptionTile?: boolean
  travellers: Array<App.Occupants>
}

function TourV2DepartureCta(props: Props) {
  const {
    type,
    offer,
    hasPrivateRequest,
    isPackageOptionTile = false,
    variant,
    travellers,
  } = props

  const supportContact = useContext(ContactContext)
  const contact = supportContact.defaultContact

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)

  const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type="TOURS" />) }

  const toggleModal = useCallback(() => {
    showModal(<PrivateRequestFormModal offer={offer} defaultTravellers={travellers} />)
  }, [showModal, offer, travellers])

  return <Group gap={12} desktopDirection="vertical" direction="vertical" fullWidth>
    {((isPackageOptionTile && type === 'soldOut') || (!isPackageOptionTile && type === 'soldOut')) &&
      <BodyTextBlock variant={variant} colour="neutral-two" startIcon={<LineExclamationCircleIcon />}>
        This departure is sold out. {isPackageOptionTile ? 'Call us for more tour options.' : 'Try a different departure or call us for more tour options.'}
      </BodyTextBlock>}

    {((isPackageOptionTile && type === 'limited') || (!isPackageOptionTile && type === 'limited')) &&
      <BodyTextBlock variant={variant} colour="neutral-two" startIcon={<LineExclamationTriangleIcon />}>
        This departure has <b>limited availability</b>. {isPackageOptionTile ? 'Call us for more options.' : 'Try a different departure or call us for more options.'}
      </BodyTextBlock>}
    <Group desktopDirection="horizontal-reverse" direction="vertical" gap={12} verticalAlign="center" horizontalAlign="center" desktopHorizontalAlign="space-between" fullWidth>

      <TextButton
        fit={isPackageOptionTile ? 'full-width' : 'mobile-full-width'}
        size="medium"
        kind="secondary"
        href={`tel:${contact.number}`}
      >
        Call on {contact.humanReadable}
      </TextButton>
      {callbacksFeatureEnabled && <TextLink
        size="medium"
        weight="bold"
        onClick={handleCallbackClick}
        startIcon={<LineIncomingCallIcon />}
      >
        Request a callback
      </TextLink>}
      {hasPrivateRequest && <TextLink size={variant} weight="regular" onClick={toggleModal}>Request private departure</TextLink>}
    </Group>
  </Group>
}

export default TourV2DepartureCta
